class BlogPostSection extends HTMLElement {
  constructor() {
    super();

    if (Shopify.designMode) {
      let elementToAdd = this.querySelector(".page__header");
      if (elementToAdd) {
        document.querySelector(".blog-container").previousElementSibling.remove();
        document.querySelector(".blog-container").insertAdjacentElement("beforebegin", elementToAdd);
      }
    }
  }

  connectedCallback() {
    this.initCopyLink();
    this.initTableOfContents();
    // Reading-progress bar: CSS-only via scroll-driven animation (animation-timeline: view()).
    // See assets/blog.css @supports block — all-majors Baseline since Safari 26 (Dec 2025).
    this.initTocScrollSpy();
  }

  disconnectedCallback() {
    if (this._spy) {
      window.removeEventListener("scroll", this._spy);
      window.removeEventListener("resize", this._spy);
    }
  }

  initCopyLink() {
    const share = this.querySelector("[data-article-share]");
    if (!share) return;

    const copyBtn = share.querySelector("[data-copy-link]");
    if (!copyBtn) return;

    const url = share.dataset.shareUrl || window.location.href;
    const labelCopy = copyBtn.dataset.labelCopy || copyBtn.getAttribute("aria-label") || "Copy link";
    const labelCopied = copyBtn.dataset.labelCopied || "Link copied";
    let resetTimer;

    const fallbackCopy = (value) => {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch (e) {
        ok = false;
      }
      document.body.removeChild(ta);
      return ok;
    };

    copyBtn.addEventListener("click", async () => {
      let ok = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          ok = true;
        }
      } catch (e) {
        ok = false;
      }
      if (!ok) ok = fallbackCopy(url);

      if (!ok) return;

      copyBtn.classList.add("is-copied");
      copyBtn.setAttribute("aria-label", labelCopied);
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        copyBtn.classList.remove("is-copied");
        copyBtn.setAttribute("aria-label", labelCopy);
      }, 2000);
    });
  }

  initTableOfContents() {
    const content = this.querySelector(".article__content");
    if (!content) return;

    let headings = Array.from(content.querySelectorAll(":scope > .article__content-block")).map((block) => {
      const title = block.querySelector(".article__content-block-title");
      return title ? { el: block, text: title.textContent.trim() } : null;
    });

    if (headings.every((h) => h === null) || headings.length === 0) {
      headings = Array.from(content.querySelectorAll("h2")).map((h2) => ({
        el: h2,
        text: h2.textContent.trim(),
      }));
    }

    headings = headings.filter((h) => h && h.text);

    if (headings.length < 2) {
      this._tocItems = [];
      this._maybeHideSidebar();
      return;
    }

    const items = headings.map((h, i) => {
      if (!h.el.id) h.el.id = "article-section-" + (i + 1);
      h.el.classList.add("is-targhet");
      return { id: h.el.id, text: h.text, el: h.el };
    });

    this._tocItems = items;

    const desktopList = this.querySelector("[data-article-toc-list]");
    const mobileList = this.querySelector("[data-article-mobile-toc-list]");
    const desktopNav = this.querySelector("[data-article-toc]");
    const mobileNav = this.querySelector("[data-article-mobile-toc]");

    const buildLi = (item) => {
      const li = document.createElement("li");
      li.className = "article__toc-item";
      const a = document.createElement("a");
      a.className = "article__toc-link";
      a.href = "#" + item.id;
      a.textContent = item.text;
      a.setAttribute("data-toc-link", "");
      a.setAttribute("data-toc-target", item.id);
      li.appendChild(a);
      return li;
    };

    [desktopList, mobileList].forEach((list) => {
      if (!list) return;
      list.innerHTML = "";
      items.forEach((it) => list.appendChild(buildLi(it)));
    });

    if (desktopNav) desktopNav.hidden = false;
    if (mobileNav) mobileNav.hidden = false;

    this._maybeHideSidebar();
  }

  _maybeHideSidebar() {
    const sidebar = this.querySelector("[data-article-sidebar]");
    const grid = this.querySelector("[data-article-grid]");
    if (!sidebar || !grid) return;
    const hasAuthor = !!sidebar.querySelector(".article__author-card");
    const hasToc = !!(this._tocItems && this._tocItems.length);
    if (!hasAuthor && !hasToc) {
      sidebar.classList.add("article__sidebar--hidden");
      grid.classList.add("article__grid--no-author");
    } else {
      sidebar.classList.remove("article__sidebar--hidden");
      grid.classList.remove("article__grid--no-author");
    }
  }

  initTocScrollSpy() {
    const links = Array.from(this.querySelectorAll("[data-toc-link]"));
    if (!links.length) return;

    const targetMap = new Map();
    links.forEach((link) => {
      const id = link.dataset.tocTarget;
      const el = id ? document.getElementById(id) : null;
      if (!el) return;
      if (!targetMap.has(id)) targetMap.set(id, { el, links: [] });
      targetMap.get(id).links.push(link);
    });

    if (!targetMap.size) return;

    const targets = Array.from(targetMap.values());

    const setActive = (activeEl) => {
      targets.forEach(({ el, links: ls }) => {
        const isActive = el === activeEl;
        ls.forEach((l) => l.classList.toggle("is-active", isActive));
      });
    };

    const offset = 100;
    let lastActive = null;
    const evaluate = () => {
      let current = null;
      for (const { el } of targets) {
        const top = el.getBoundingClientRect().top;
        if (top - offset <= 0) {
          current = el;
        } else {
          break;
        }
      }
      if (!current && targets.length) current = targets[0].el;
      if (current !== lastActive) {
        lastActive = current;
        setActive(current);
      }
    };

    this._spy = evaluate;
    window.addEventListener("scroll", evaluate, { passive: true });
    window.addEventListener("resize", evaluate, { passive: true });
    evaluate();

    links.forEach((link) => {
      link.addEventListener("click", (e) => {
        const id = link.dataset.tocTarget;
        const target = id ? document.getElementById(id) : null;
        if (!target) return;
        e.preventDefault();
        // scrollIntoView honours the target's scroll-margin-top (.is-targhet),
        // so the click path matches a native #hash navigation and clears the sticky header.
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        history.pushState(null, "", "#" + id);

        const mobileToc = link.closest("[data-article-mobile-toc]");
        if (mobileToc && mobileToc.open) mobileToc.open = false;
      });
    });
  }
}

if (!window.customElements.get("cos-blog-post")) {
  window.customElements.define("cos-blog-post", BlogPostSection);
}
